import React, { useContext, useDeferredValue, useMemo } from "react";
import { FixedSizeList as List } from "react-window";
import { useWindowSize } from "usehooks-ts";
import { PublisherAdvancedFilters } from "@seabrookstudios/pitch2table-core";
import { usePublisherFitForGameV2 } from "@seabrookstudios/pitch2table";
import { ListView } from "../../core/ListView";
import { useFiltersv2 } from "../../core/useFilters";
import { applyAdvancedFilters } from "../../core/applyAdvancedFilters";
import { useAdvancedFilters } from "../../core/useAdvancedFilters";
import { useMergedPublishers } from "../../publishers/use-merged-publishers";
import { ShortlistPublisherFilterContext } from "./ShortlistPublisherFilter";
import { publisherNameMatchesFilter } from "./publisherNameFilter";

const VIRTUAL_ITEM_SIZE = 72;
const VIRTUAL_LIST_HEIGHT_OFFSET = 360;

/**
 * Merged publishers plus per-row fit score (index 2), sorted by fit descending.
 *
 * @param {import("@seabrookstudios/types").GameId} gameId
 */
const useResearchPublishersWithFit = (gameId) => {
  const merged = useMergedPublishers();
  const fitQuery = usePublisherFitForGameV2(gameId);
  const fitRows = fitQuery.data ?? [];

  const data = useMemo(() => {
    const base = merged.data ?? [];
    const fitByPublisherId = new Map(fitRows.map((row) => [row.publisherId, row]));
    const withFit = base.map((tuple) => {
      const [publisher, myPublisher] = tuple;
      const fitRow = fitByPublisherId.get(publisher.id);
      return [publisher, myPublisher, fitRow?.score, fitRow?.label];
    });
    return [...withFit].sort((a, b) => {
      const aScore = typeof a[2] === "number" ? a[2] : 0;
      const bScore = typeof b[2] === "number" ? b[2] : 0;
      return bScore - aScore;
    });
  }, [merged.data, fitRows]);

  return {
    data,
    refetch: async () => {
      await Promise.all([merged.refetch(), fitQuery.refetch()]);
    },
    isLoading: merged.isLoading || fitQuery.isLoading,
    isRefetching: merged.isRefetching || fitQuery.isRefetching,
    isError: Boolean(fitQuery.isError),
  };
};

/**
 * Virtualized publisher research list with name search and advanced filters.
 * Must render inside ShortlistPublisherFilterProvider.
 *
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {({ item, style }) => React.JSX.Element} props.ListEntry
 * @param {() => React.JSX.Element} props.ListHeader
 * @param {() => React.JSX.Element} props.EmptyList
 * @param {() => React.JSX.Element} props.FilteredEmptyList
 * @param {(any) => React.JSX.Element} props.Action
 */
export const PublisherResearchListView = ({ gameId, ListEntry, ListHeader, EmptyList, FilteredEmptyList, Action }) => {
  const queryResult = useResearchPublishersWithFit(gameId);
  const filterConfiguration = useFiltersv2(/** @type {any} */ (queryResult), [], "publisher-research");
  const { height: windowHeight } = useWindowSize();
  const { filter: nameFilter = "" } = useContext(ShortlistPublisherFilterContext);
  const deferredNameFilter = useDeferredValue(nameFilter);

  const visibleAdvancedFilters = useMemo(
    () => PublisherAdvancedFilters.filter((f) => (f.hideOn ? !f.hideOn.includes("web") : true)),
    []
  );

  const { selectionsByLabel: advancedSelectionsByLabel, setSelectionsByLabel: setAdvancedSelectionsByLabel } =
    useAdvancedFilters(visibleAdvancedFilters);

  const itemsBeforeNameFilter = useMemo(() => {
    if (visibleAdvancedFilters.length === 0) {
      return filterConfiguration.filtered;
    }
    return applyAdvancedFilters(filterConfiguration.filtered, visibleAdvancedFilters, advancedSelectionsByLabel);
  }, [filterConfiguration.filtered, visibleAdvancedFilters, advancedSelectionsByLabel]);

  const publishersToShow = useMemo(() => {
    if (!deferredNameFilter) {
      return itemsBeforeNameFilter;
    }
    return itemsBeforeNameFilter.filter((item) =>
      publisherNameMatchesFilter(
        /** @type {Parameters<typeof publisherNameMatchesFilter>[0]} */ (item),
        deferredNameFilter
      )
    );
  }, [itemsBeforeNameFilter, deferredNameFilter]);

  const listHeight = Math.max(200, windowHeight - VIRTUAL_LIST_HEIGHT_OFFSET);

  const ResolvedEmptyList = () => {
    if (deferredNameFilter && publishersToShow.length === 0 && itemsBeforeNameFilter.length > 0) {
      return FilteredEmptyList();
    }

    return EmptyList();
  };

  const ListEntryWrapper = ({ style, index, data }) => {
    const item = data[index];
    return <ListEntry item={item} style={style} />;
  };

  return (
    <ListView
      title="Publisher Research"
      Action={Action}
      EmptyList={ResolvedEmptyList}
      shown={publishersToShow.length}
      refetch={queryResult.refetch}
      isLoading={queryResult.isLoading}
      isRefetching={queryResult.isRefetching}
      isError={queryResult.isError}
      advancedFilters={visibleAdvancedFilters}
      advancedFilterSelectionsByLabel={advancedSelectionsByLabel}
      onAdvancedFilterSelectionsByLabelChange={setAdvancedSelectionsByLabel}
      listHeader={<ListHeader />}
    >
      <List
        height={listHeight}
        itemCount={publishersToShow.length}
        itemSize={VIRTUAL_ITEM_SIZE}
        itemData={publishersToShow}
        width="100%"
      >
        {ListEntryWrapper}
      </List>
    </ListView>
  );
};
